/**
 * M1-25 — the subscription screen (§7.3, §7.21).
 *
 * The acceptance is the hard part: *an unpaid owner is told plainly what is
 * blocked and why.* That rules out a status word and a Pay button, which is
 * what a billing screen usually is. An owner whose till has stopped needs two
 * answers in the same breath — what has stopped, and what has not — because
 * the second is the one that determines whether they panic about their data.
 *
 * §16.16 is the source of both lists, and `capabilities` on the subscription
 * is the server's own answer rather than this screen's guess, so the portal
 * cannot drift from what the service will actually allow.
 */

import { useCallback, useEffect, useState } from 'react'

import { api, type Payment, type Subscription } from '../api.js'
import { date, money, relativeDays } from '../format.js'
import { Alert, Card, Consequences, Pill, Row, Rows, StatusPill } from '../components/ui.js'
import { messageFor } from './Register.js'
import { consequencesOf, whyBlocked } from '../subscription.js'

export function Billing() {
  const [subscription, setSubscription] = useState<Subscription | null>(null)
  const [payments, setPayments] = useState<Payment[]>([])
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const load = useCallback(async () => {
    const [s, p] = await Promise.all([api.subscription(), api.payments()])
    setSubscription(s)
    setPayments(p.payments)
  }, [])

  useEffect(() => {
    void load().catch((err: unknown) => setError(messageFor(err)))
  }, [load])

  async function pay() {
    setBusy(true)
    setError(null)
    try {
      const checkout = await api.startCheckout()
      // Full navigation, not a router push: Paystack is another origin.
      window.location.assign(checkout.authorizationUrl)
    } catch (err) {
      setError(messageFor(err))
      setBusy(false)
    }
  }

  if (error && !subscription) return <Alert>{error}</Alert>
  if (!subscription) return <p className="empty">Loading…</p>

  const active = subscription.status === 'active'
  const trial = subscription.status === 'trial'
  const why = whyBlocked(subscription)
  const { blocked, working } = consequencesOf(subscription)

  return (
    <>
      {error ? <Alert>{error}</Alert> : null}

      <Card title="Subscription" action={<StatusPill subscription={subscription} />}>
        {active && subscription.daysRemaining !== null ? (
          <p>
            Paid through <strong>{date(subscription.paidThrough)}</strong> —{' '}
            {relativeDays(subscription.daysRemaining, false)} left.
          </p>
        ) : null}
        {why ? <p>{why}</p> : null}
        {trial && subscription.trialDaysRemaining !== null ? (
          <p>
            Free trial — <strong>{subscription.trialDaysRemaining}</strong> day
            {subscription.trialDaysRemaining === 1 ? '' : 's'} left.
          </p>
        ) : null}

        <Rows>
          <Row label="Plan">{money(subscription.priceCents)} per month</Row>
          <Row label="Devices included">{subscription.deviceAllowance}</Row>
          <Row label="Paid through">
            {subscription.paidThrough ? date(subscription.paidThrough) : 'Never paid'}
          </Row>
          {active && subscription.daysRemaining !== null ? (
            <Row label="Remaining">{relativeDays(subscription.daysRemaining, false)}</Row>
          ) : null}
          {subscription.status === 'lapsed' ? (
            <Row label="Lapsed">{relativeDays(subscription.daysLapsed ?? 0, true)}</Row>
          ) : null}
          <Row label="Included features">
            {subscription.entitlements.length === 0 ? (
              // Not an error and not an empty cell: the base plan is the whole
              // product in this release, and "none" would read like something
              // is missing.
              <span className="muted">Everything in the base plan</span>
            ) : (
              <span className="chips">
                {subscription.entitlements.map((flag) => (
                  <Pill key={flag} tone="muted">
                    {flag}
                  </Pill>
                ))}
              </span>
            )}
          </Row>
        </Rows>

        <div className="actions">
          <button type="button" className="primary" onClick={() => void pay()} disabled={busy}>
            {busy ? 'Opening payment…' : active ? 'Pay for another month' : 'Pay now'}
          </button>
        </div>

        {active ? (
          <p className="card__note" style={{ margin: '0.7rem 0 0' }}>
            Paying early extends your cover rather than restarting it — you keep the days you
            have already paid for.
          </p>
        ) : null}
      </Card>

      {subscription.status !== 'active' && !trial ? (
        <Card
          title="What this means"
          note="Paying restores everything on this list straight away. Your records are not affected either way."
        >
          <Consequences blocked={blocked} working={working} />
        </Card>
      ) : null}

      <Card title="Payments" note="Every payment recorded against this account, however it was made.">
        {payments.length === 0 ? (
          <p className="empty">No payments yet.</p>
        ) : (
          <Rows>
            {payments.map((payment) => (
              <Row key={payment.reference} label={date(payment.paidAt ?? payment.createdAt)}>
                {money(payment.amountCents, payment.currency)}{' '}
                {payment.status === 'succeeded' ? (
                  <Pill tone="ok">Paid</Pill>
                ) : payment.status === 'pending' ? (
                  <Pill tone="warn">Pending</Pill>
                ) : (
                  <Pill tone="muted">Failed</Pill>
                )}
                {payment.periodEnd && payment.status === 'succeeded' ? (
                  <span className="devices__meta"> · covers to {date(payment.periodEnd)}</span>
                ) : null}
              </Row>
            ))}
          </Rows>
        )}
      </Card>
    </>
  )
}
